// Vendor imports
const classNames = require('classnames')
const PropTypes = require('prop-types')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
// Client V2 common imports
const { CapsuleButton, CircleButton } = require('client-v2/common/components/buttons')
const { ModalDepthModifier } = require('client-v2/common/contexts/modal-depth')
const CollectionModelRecord = require('client-v2/common/models/collection/record')
const tag = require('client-v2/common/utils/hyperscript')
// Sibling imports
const AddToCollectionItem = require('./item')

const MAX_COLLECTION_NAME_LENGTH = 50

const bem = BemEntity('AddToCollectionModal')

class AddToCollectionModal extends React.PureComponent {
  constructor(props) {
    super(props)

    this.state = {
      hover: false
    }

    this.modalRef = React.createRef()
    this.filterInputRef = React.createRef()

    // Bind event listeners
    this.onDismissCapture = this.onDismissCapture.bind(this)
    this.onFilterChange = this.onFilterChange.bind(this)
    this.onCollectionClick = this.onCollectionClick.bind(this)
    this.onCreateClick = this.onCreateClick.bind(this)
    this.onMouseEnter = () => this.setState({ hover: true })
    this.onMouseLeave = () => this.setState({ hover: false })
  }

  componentDidMount() {
    if (typeof window !== 'undefined') {
      document.addEventListener('scroll', this.onDismissCapture, true)
      document.addEventListener('click', this.onDismissCapture, true)
    }
  }

  componentWillUnmount() {
    if (typeof document !== 'undefined') {
      document.removeEventListener('scroll', this.onDismissCapture, true)
      document.removeEventListener('click', this.onDismissCapture, true)
    }
  }

  componentDidUpdate() {
    if (this.props.onLayoutChange) {
      this.props.onLayoutChange()
    }
  }

  // Event handler methods

  onDismissCapture(event) {
    const modalEl = this.modalRef.current
    if (!modalEl) {
      return
    }
    // Prevent auto-dismissal when the filter input has focus
    if (document.activeElement === this.filterInputRef.current) {
      return
    }
    let traverseEl = event.target
    while (traverseEl) {
      if (traverseEl === modalEl) {
        return
      }
      if (
        // Allow clicks on ArticleMixButton to toggle the modal
        event.type === 'click' &&
        traverseEl.classList.contains('ArticleMixButton')
      ) {
        return
      }
      traverseEl = traverseEl.parentElement
    }
    this.props.onDismiss()
  }

  onModalClick(event) {
    // Swallow events to prevent clicks going through/behind the modal
    event.preventDefault()
    event.stopPropagation()
  }

  onFilterChange() {
    const inputEl = this.filterInputRef.current
    this.props.onFilterTextChange(inputEl.value)
  }

  onCollectionClick(collection) {
    this.props.onCollectionClick({ collection })
  }

  onCreateClick(event) {
    event.preventDefault()
    this.props.onCreate()
  }

  // Component render methods

  renderHeader() {
    return (
      tag('header', {
        className: bem.element('header')
      },
        tag('div', {
          className: bem.element('title')
        },
          'Add to a collection'
        ),
        tag(CircleButton, {
          className: bem.element('close-button'),
          onClick: this.props.onDismiss
        })
      )
    )
  }

  renderFilterForm() {
    return (
      tag('form', {
        className: bem.element('filter-form'),
        onSubmit: this.onCreateClick
      },
        tag('div', {
          className: bem.element('filter-input')
        },
          tag('input', {
            ref: this.filterInputRef,
            className: bem.element('filter-text'),
            autoComplete: 'off',
            autoCorrect: 'off',
            autoCapitalize: 'off',
            autoFocus: true,
            disabled: !this.props.isCollectionsLoaded,
            maxLength: MAX_COLLECTION_NAME_LENGTH,
            placeholder: 'Search for or create a collection',
            spellCheck: 'false',
            tabIndex: 1,
            type: 'text',
            value: this.props.filterText,
            onChange: this.onFilterChange
          }),
          tag('div', {
            className: bem.element('search-icon')
          })
        ),
        tag(CapsuleButton, {
          className: bem.element('filter-submit', {
            disabled: this.props.isCreateButtonDisabled,
            hidden: !this.props.isCreateButtonVisible
          }),
          disabled: this.props.isCreateButtonDisabled,
          onClick: this.onCreateClick
        },
          'Create'
        )
      )
    )
  }

  renderCollectionsList() {
    if (!this.props.isCollectionsLoaded) {
      return (
        tag('div', {
          className: bem.element('list', 'loading')
        })
      )
    }
    if (this.props.isUnfilteredCollectionsEmpty) {
      return this.renderCreatePrompt()
    }
    if (this.props.isFilteredCollectionsEmpty) {
      return this.renderEmptySearch()
    }
    return (
      tag('div', {
        className: bem.element('list')
      },
        this.props.collections.map(item => (
          tag(AddToCollectionItem, {
            key: item.slug,
            className: bem.element('list-item'),
            item,
            onClick: this.onCollectionClick
          })
        ))
      )
    )
  }

  renderInnerCollectionsError() {
    return (
      tag('div', {
        className: bem.element('error')
      },
        tag('p', {
          className: bem.element('error-heading')
        },
          'Oops.'
        ),
        tag('p', {
          className: bem.element('error-subheading')
        },
          'Something went wrong. Give it another shot.'
        ),
        tag(CapsuleButton, {
          className: bem.element('error-retry'),
          onClick: this.props.onRetry
        },
          'Retry'
        ),
        tag(CapsuleButton, {
          className: bem.element('error-contact'),
          href: 'mailto:support@mix.com',
          onClick: this.onSupportClick
        },
          'Contact Support'
        )
      )
    )
  }

  renderCreatePrompt() {
    return (
      tag('div', {
        className: bem.element('create-prompt')
      },
        tag('div', {
          className: bem.element('arrow')
        }),
        tag('div', {
          className: bem.element('create-text')
        },
          'Create your first collection to ',
          'organize cool things to save.'
        )
      )
    )
  }

  renderEmptySearch() {
    return (
      tag('div', {
        className: bem.element('empty-search')
      },
        'No search results... Create ',
        tag('a', {
          className: bem.element('empty-search-create'),
          onClick: this.onCreateClick
        },
          `“${this.props.filterText}”?`
        )
      )
    )
  }

  renderSnackbar() {
    const { snackbarIcon, snackbarText } = this.props
    return (
      tag('div', {
        className: bem.element('snackbar')
      },
        snackbarIcon && tag('div', {
          className: bem.element('snackbar-icon', {
            [this.props.snackbarIcon]: true
          })
        }),
        tag('div', {
          className: bem.element('snackbar-text')
        },
          snackbarText
        )
      )
    )
  }

  renderUnmix() {
    const { isUnmixDisabled } = this.props
    return (
      tag('button', {
        className: bem.element('unmix-button', {
          disabled: isUnmixDisabled
        }),
        disabled: isUnmixDisabled,
        onClick: this.props.onUnmix
      },
        'Unmix'
      )
    )
  }

  renderFooter() {
    if (this.props.isSnackbarVisible) {
      return this.renderSnackbar()
    }
    if (this.props.isUnmixVisible) {
      return this.renderUnmix()
    }
    return null
  }

  renderUnmixPrompt() {
    const { isUnmixDisabled } = this.props
    return (
      tag('div', {
        className: bem.element('inner', 'unmix')
      },
        tag('p', {
          className: bem.element('unmix-prompt')
        },
          'Do you want to remove this post from your profile?'
        ),
        tag(CapsuleButton, {
          className: bem.element('unmix-prompt-confirm'),
          disabled: isUnmixDisabled,
          onClick: this.props.onUnmixPromptConfirm
        },
          'Remove post'
        ),
        tag(CapsuleButton, {
          className: bem.element('unmix-prompt-cancel'),
          disabled: isUnmixDisabled,
          onClick: this.props.onUnmixPromptCancel
        },
          'Keep post'
        )
      )
    )
  }

  renderInner() {
    if (this.props.isUnmixPromptVisible) {
      return this.renderUnmixPrompt()
    }
    if (this.props.isCollectionsFailed) {
      return this.renderInnerCollectionsError()
    }
    return (
      tag('div', {
        className: bem.element('inner')
      },
        this.renderHeader(),
        this.renderFilterForm(),
        this.renderCollectionsList(),
        this.renderFooter()
      )
    )
  }

  // Root render method

  render() {
    if (!this.props.isVisible) {
      return null
    }
    return (
      tag('div', {
        ref: this.modalRef,
        className: classNames(
          this.props.className,
          bem.block()
        ),
        onClick: this.onModalClick,
        onMouseEnter: this.onMouseEnter,
        onMouseLeave: this.onMouseLeave
      },
        this.state.hover && tag(ModalDepthModifier),
        this.renderInner()
      )
    )
  }
}

AddToCollectionModal.propTypes = {
  // Flags
  isCreateButtonDisabled: PropTypes.bool,
  isCreateButtonVisible: PropTypes.bool,
  isCollectionsFailed: PropTypes.bool,
  isCollectionsLoaded: PropTypes.bool,
  isFilteredCollectionsEmpty: PropTypes.bool,
  isSnackbarVisible: PropTypes.bool,
  isUnfilteredCollectionsEmpty: PropTypes.bool,
  isUnmixDisabled: PropTypes.bool,
  isUnmixPromptVisible: PropTypes.bool,
  isUnmixVisible: PropTypes.bool,
  isVisible: PropTypes.bool,
  // Values
  className: PropTypes.string,
  collections: PropTypes.arrayOf(
    PropTypes.shape(CollectionModelRecord.propTypes)
  ),
  filterText: PropTypes.string,
  snackbarIcon: PropTypes.string,
  snackbarText: PropTypes.string,
  // Callbacks
  onCollectionClick: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  onFilterTextChange: PropTypes.func.isRequired,
  onLayoutChange: PropTypes.func,
  onRetry: PropTypes.func.isRequired,
  onUnmix: PropTypes.func.isRequired,
  onUnmixPromptCancel: PropTypes.func.isRequired,
  onUnmixPromptConfirm: PropTypes.func.isRequired
}

module.exports = AddToCollectionModal
